import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import {
  AGENTS_ENGINE_ACCESSES,
  AGENTS_ENGINE_MODULE_KEY,
} from '../constants/agents-engine-access';
import { CustomNotificationService } from './custom.notification.service';
import { OrgModulesService } from './org-modules.service';
import { PermissionService } from './permissions.service';

export type AgentsEngineDenial = 'module' | 'access';

/** Texto del aviso «Sin acceso» según lo que falte (mismo patrón que el resto de guards de módulo). */
export const AGENTS_ENGINE_DENIAL_MESSAGES: Readonly<Record<AgentsEngineDenial, string>> = {
  module: 'Tu organización no tiene habilitado el módulo de Agentes IA.',
  access:
    'Tu usuario no tiene ningún acceso de Agentes IA (agents_engine_*). Pide a un administrador de tu organización que te asigne alguno y vuelve a entrar.',
};

/**
 * Protege `/pages/agents-engine` (feature nativa de Agentes IA): la organización debe tener habilitado el
 * módulo `agents-engine` en `org_modules` (viene en el JWT como `orgModules`) y el usuario alguno de los
 * accesos `agents_engine_*` (misma condición que la entrada del menú, `PagesComponent.canAccessAgentsEngine`).
 * Sin ello: aviso «Sin acceso» y vuelta al dashboard. Dentro de la feature los scopes concretos de cada
 * pantalla los explica `<ae-scope-gate>`; la autoridad es la API.
 */
@Injectable({
  providedIn: 'root',
})
export class AgentsEngineModuleGuard implements CanActivate {
  constructor(
    private readonly orgModulesService: OrgModulesService,
    private readonly permissionService: PermissionService,
    private readonly router: Router,
    private readonly notification: CustomNotificationService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const denial = this.denial();
    if (!denial) {
      return true;
    }

    this.notification.show(
      'warning',
      'Sin acceso',
      AGENTS_ENGINE_DENIAL_MESSAGES[denial],
      'topRight',
    );
    this.router.navigate(['/pages/dashboard'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }

  /** Qué falta para entrar: el módulo, algún access, o nada (`null`). */
  private denial(): AgentsEngineDenial | null {
    if (!this.orgModulesService.hasAccessToModule(AGENTS_ENGINE_MODULE_KEY)) return 'module';
    if (!this.permissionService.hasAnyPermission([...AGENTS_ENGINE_ACCESSES])) return 'access';
    return null;
  }
}
